import { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { AlertTriangle, FileSpreadsheet, Loader2, Upload, X } from 'lucide-react'

import {
  dealBookImportService,
  PREVIEW_TIMEOUT_MS,
  type DealBookAction,
  type DealBookResult,
} from '@/services/dealBookImport'

import DealBookPreviewGrid from './DealBookPreviewGrid'
import { isProblemRow } from './dealBookRows'

/** The server's own limits (`deal_book_import.MAX_BOOK_FILES`, `MAX_WORKBOOK_BYTES`):
 *  checked here first so a file it would refuse is refused at once, with the reason. */
const MAX_FILES = 4
const MAX_FILE_BYTES = 10 * 1024 * 1024
/** After this long the status line also says a large book takes a while. */
const SLOW_AFTER_SECONDS = 20

function megabytes(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/** Why a chosen file cannot be read, before it is sent; null when it can be. */
function unreadableFileReason(file: File): string | null {
  const extension = file.name.includes('.') ? file.name.split('.').pop()!.toLowerCase() : ''
  if (extension === 'xml') {
    return `${file.name} is an XML export. The import reads Excel workbooks (.xlsx) today; run the Backshop report to Excel and choose that file.`
  }
  if (extension !== 'xlsx') {
    const kind = extension ? `a .${extension} file` : 'not an Excel workbook'
    return `${file.name} is ${kind}. The import reads Excel workbooks (.xlsx): open it in Excel, Save As "Excel Workbook (.xlsx)", then choose it again.`
  }
  if (file.size > MAX_FILE_BYTES) {
    return `${file.name} is ${megabytes(file.size)}; a deal book is at most ${megabytes(MAX_FILE_BYTES)}.`
  }
  if (file.size === 0) return `${file.name} is empty.`
  return null
}

/** What a failed preview means, in a sentence: the server's reason when it gave one. */
function previewFailure(err: unknown): string {
  const failure = err as { code?: string; response?: { data?: { detail?: unknown } } }
  const detail = failure?.response?.data?.detail
  if (typeof detail === 'string') return detail
  if (failure?.code === 'ECONNABORTED') {
    return `The server did not answer within ${PREVIEW_TIMEOUT_MS / 60_000} minutes. Nothing was written — choose the files again to retry.`
  }
  if (!failure?.response) {
    return 'The server could not be reached. Nothing was written — check the connection, then choose the files again.'
  }
  return 'The workbook could not be read. Nothing was written.'
}

const ACTION_LABEL: Record<DealBookAction, string> = {
  create: 'to create',
  exists: 'already in Helios',
  skip: 'skipped (Funded)',
  reject: 'not imported',
}

/** Which rows the preview shows: every deal, one outcome, or the rows that need a look. */
type DealBookFilter = 'all' | 'problems' | DealBookAction

const ACTION_TONE: Record<DealBookAction, string> = {
  create: 'text-emerald-300',
  exists: 'text-stone-400',
  skip: 'text-stone-400',
  reject: 'text-orange-300',
}

/**
 * Upload a Backshop deal book (the Status / Pipeline workbook). The server reads
 * it and previews every deal; nothing is written until the previewed file is
 * committed. Admin only, like placing a deal past Triage.
 */
export default function DealBookImportDialog({
  open,
  onClose,
  onCommitted,
}: {
  open: boolean
  onClose: () => void
  onCommitted: () => void | Promise<void>
}) {
  const [files, setFiles] = useState<File[]>([])
  const [preview, setPreview] = useState<DealBookResult | null>(null)
  const [result, setResult] = useState<DealBookResult | null>(null)
  const [busy, setBusy] = useState(false)
  // What the dialog is waiting on, and since when, so a long read or a long
  // create says so and counts up instead of looking frozen (Chris, 26 Oct: "It's frozen").
  const [waiting, setWaiting] = useState<{ what: 'reading' | 'creating'; since: number; files: number; bytes: number; deals: number } | null>(null)
  const [now, setNow] = useState(() => Date.now())
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<DealBookFilter>('all')
  const fileRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (!waiting) return
    setNow(Date.now())
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [waiting])
  // Bumped on every reset: a preview that comes back after the dialog was closed
  // belongs to the book that was cancelled and must not reappear.
  const generation = useRef(0)
  if (!open) return null

  // Cancel and Close leave nothing behind: the next open is an empty dialog
  // waiting for a new workbook, not the last book's preview or result.
  function close() {
    generation.current += 1
    setFiles([])
    setPreview(null)
    setResult(null)
    setBusy(false)
    setWaiting(null)
    setError(null)
    setFilter('all')
    if (fileRef.current) fileRef.current.value = ''
    onClose()
  }

  // Each file chosen joins the ones already chosen (a file of the same name is replaced), so the Status
  // report and the Pipeline report can be picked together or one after the other. Cancel starts over.
  async function choose(added: File[]) {
    if (!added.length) return
    // A file the server would refuse is refused here, at once and with the reason,
    // instead of after an upload; the files already chosen stay as they were.
    const refused = added.map(unreadableFileReason).filter((reason): reason is string => reason !== null)
    if (refused.length) {
      setError(refused.join(' '))
      return
    }
    const byName = new Map(files.map((file) => [file.name, file]))
    for (const file of added) byName.set(file.name, file)
    if (byName.size > MAX_FILES) {
      setError(`Choose at most ${MAX_FILES} files: the Status report and the Pipeline report, or one workbook holding both.`)
      return
    }
    await review([...byName.values()])
  }

  async function removeFile(name: string) {
    const rest = files.filter((file) => file.name !== name)
    if (rest.length) {
      await review(rest)
      return
    }
    generation.current += 1
    setFiles([])
    setPreview(null)
    setResult(null)
    setError(null)
    setBusy(false)
    setWaiting(null)
  }

  async function review(chosen: File[]) {
    const run = ++generation.current
    setFiles(chosen)
    setPreview(null)
    setResult(null)
    setError(null)
    setFilter('all')
    setBusy(true)
    setWaiting({
      what: 'reading', since: Date.now(), files: chosen.length,
      bytes: chosen.reduce((sum, file) => sum + file.size, 0), deals: 0,
    })
    try {
      const previewed = await dealBookImportService.preview(chosen)
      if (run === generation.current) setPreview(previewed)
    } catch (err: unknown) {
      if (run !== generation.current) return
      setError(previewFailure(err))
    } finally {
      if (run === generation.current) {
        setBusy(false)
        setWaiting(null)
      }
    }
  }

  async function commit() {
    if (!files.length || !preview?.preview_token) return
    const run = generation.current
    setBusy(true)
    setError(null)
    setWaiting({ what: 'creating', since: Date.now(), files: files.length, bytes: 0, deals: preview.counts.create })
    try {
      const done = await dealBookImportService.commit(files, preview.preview_token)
      // Deals were created even if the dialog was closed meanwhile: the board
      // still refreshes; only this dialog's own state is left alone.
      if (run === generation.current) {
        setResult(done)
        setPreview(null)
      }
      await onCommitted()
    } catch {
      if (run !== generation.current) return
      setPreview(null)
      setError('The book changed since the preview, or the commit failed. Choose the file again to re-preview.')
    } finally {
      if (run === generation.current) {
        setBusy(false)
        setWaiting(null)
      }
    }
  }

  const waitedSeconds = waiting ? Math.max(0, Math.floor((now - waiting.since) / 1000)) : 0

  const shown = result ?? preview
  // After a commit a planned "to create" row is either created or not: say which.
  const createdNames = new Set(result?.created?.map((c) => c.deal) ?? [])
  const failedNames = new Set(result?.failed?.map((f) => f.deal) ?? [])
  function outcome(deal: { deal: string; action: DealBookAction }): { label: string; tone: string } {
    if (result && deal.action === 'create') {
      if (createdNames.has(deal.deal)) return { label: 'created', tone: 'text-emerald-300' }
      if (failedNames.has(deal.deal)) return { label: 'not created', tone: 'text-red-300' }
    }
    return { label: ACTION_LABEL[deal.action], tone: ACTION_TONE[deal.action] }
  }
  // Compact until a workbook is chosen; then the whole book is laid out, so the dialog takes the window.
  const wide = files.length > 0 || Boolean(shown)
  const shownDeals = shown?.deals ?? []
  const withGaps = shownDeals.filter((deal) => deal.action === 'create' && (deal.gaps ?? []).length > 0).length
  const withWarnings = shownDeals.filter((deal) => deal.action === 'create' && deal.warnings.length > 0).length
  const problems = shownDeals.filter(isProblemRow).length
  const visible = shownDeals.filter((deal) => {
    if (filter === 'all') return true
    if (filter === 'problems') return isProblemRow(deal)
    return deal.action === filter
  })
  const filters: { id: DealBookFilter; label: string; count: number; note?: string; tone?: string }[] = shown ? [
    { id: 'all', label: 'All deals', count: shownDeals.length },
    { id: 'create', label: 'To create', count: shown.counts.create, tone: ACTION_TONE.create },
    { id: 'exists', label: 'Already in Helios', count: shown.counts.exists, note: 'never changed' },
    ...(shown.counts.reject ? [{ id: 'reject' as const, label: 'Not imported', count: shown.counts.reject, tone: ACTION_TONE.reject }] : []),
    ...(shown.counts.skip ? [{ id: 'skip' as const, label: 'Skipped', count: shown.counts.skip }] : []),
    {
      id: 'problems', label: 'Problems', count: problems, tone: problems ? 'text-amber-200' : undefined,
      note: `${withGaps} with gaps · ${withWarnings} with warnings`,
    },
  ] : []
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-2 sm:p-4" role="dialog" aria-modal="true" aria-label="Import deal book">
      <div
        data-testid="deal-book-dialog"
        data-layout={wide ? 'wide' : 'compact'}
        className={clsx(
          'flex w-full min-w-0 flex-col rounded-xl border border-amber-400/20 bg-stone-950 shadow-2xl',
          wide ? 'h-full' : 'max-h-[90vh] max-w-4xl',
        )}
      >
        <div className="flex shrink-0 items-start justify-between border-b border-amber-400/10 p-4">
          <div>
            <h2 className="text-sm font-medium text-stone-100">Import deal book</h2>
            <p className="mt-1 text-[11px] text-stone-500">
              Choose the Backshop Status and Pipeline reports together (select both files), or one workbook that holds both sheets (.xlsx). Every deal is previewed first; nothing is written until you commit.
              Existing deals are never changed; Funded deals come in as Funded loans, dated from the Status sheet. A deal with a datapoint the file does not state is still created, with it blank, and opens in the Data Intelligence Review for a person to fill in.
            </p>
          </div>
          <button type="button" aria-label="Close import" className="text-stone-500 hover:text-stone-200" onClick={close}><X size={16} /></button>
        </div>
        <div className={clsx('flex min-h-0 flex-1 flex-col gap-3 p-4', wide ? 'overflow-hidden' : 'overflow-y-auto')}>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <button type="button" className="btn-secondary flex items-center gap-1.5 text-[11px]" onClick={() => fileRef.current?.click()} disabled={busy}>
              <Upload size={12} /> Choose workbook(s)
            </button>
            <input ref={fileRef} data-testid="deal-book-file" className="hidden" type="file" accept=".xlsx" multiple onChange={(event) => { void choose(Array.from(event.target.files ?? [])); event.target.value = '' }} />
            {files.length ? (
              <ul className="flex flex-wrap items-center gap-1.5" aria-label="Chosen files">
                {files.map((file) => (
                  <li key={file.name} className="flex items-center gap-1 rounded border border-white/[0.08] px-1.5 py-0.5 text-[10px] text-stone-300">
                    {file.name}
                    <button type="button" aria-label={`Remove ${file.name}`} className="text-stone-500 hover:text-stone-200 disabled:opacity-40" disabled={busy} onClick={() => void removeFile(file.name)}>
                      <X size={10} />
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <span className="text-[10px] text-stone-500">No file chosen</span>
            )}
          </div>
          {waiting ? (
            <p role="status" aria-live="polite" className="flex shrink-0 items-start gap-2 rounded border border-amber-400/20 bg-amber-400/[0.04] p-2 text-[11px] text-amber-100/90" data-testid="deal-book-waiting">
              <Loader2 size={12} className="mt-0.5 shrink-0 animate-spin text-amber-300" aria-hidden="true" />
              <span>
                {waiting.what === 'reading'
                  ? `Reading ${waiting.files} file${waiting.files === 1 ? '' : 's'} (${megabytes(waiting.bytes)}) and checking every deal… ${waitedSeconds}s.`
                  : `Creating ${waiting.deals} deal${waiting.deals === 1 ? '' : 's'}… ${waitedSeconds}s. Keep this window open until it finishes.`}
                {waiting.what === 'reading' && waitedSeconds >= SLOW_AFTER_SECONDS
                  ? ' A large book can take a minute or two; nothing is written until you choose Create.'
                  : ''}
              </span>
            </p>
          ) : null}
          {!result && files.length === 1 && preview?.deals.some((deal) => deal.reasons.some((reason) => reason.includes('Backshop code'))) ? (
            <p role="status" className="shrink-0 rounded border border-amber-400/20 bg-amber-400/[0.04] p-2 text-[11px] text-amber-100/90" data-testid="deal-book-add-pipeline">
              This file gives each deal's status only as a code. Choose the Pipeline report too: it names the statuses, and the two are joined by deal name.
            </p>
          ) : null}
          {shown && (
            <div className="flex min-h-0 flex-1 flex-col gap-2" data-testid="deal-book-preview">
              {result ? (
                <div className="shrink-0 rounded border border-emerald-400/15 bg-emerald-400/[0.04] p-2 text-[11px] text-emerald-200">
                  Created {result.created?.length ?? 0} deal{result.created?.length === 1 ? '' : 's'}.
                  {result.failed?.length ? ` ${result.failed.length} could not be created (listed below).` : ''}
                  {result.needs_review ? (
                    <span data-testid="deal-book-review-link">
                      {' '}{result.needs_review} open{result.needs_review === 1 ? 's' : ''} in the Data Intelligence Review for missing datapoints:{' '}
                      <a href="/intake?review=1" className="underline">Open it</a>.
                    </span>
                  ) : null}
                </div>
              ) : null}
              {!result && preview?.needs_review ? (
                <p className="shrink-0 rounded border border-amber-400/20 bg-amber-400/[0.04] p-2 text-[11px] text-amber-100/90" data-testid="deal-book-needs-review">
                  {preview.needs_review} of the deals to create {preview.needs_review === 1 ? 'is' : 'are'} missing datapoints the file does not state. They are created with those blank, nothing guessed, and open in the Data Intelligence Review for a person to populate before they move on.
                </p>
              ) : null}
              {result?.created?.some((c) => c.warning) ? (
                <div className="max-h-24 shrink-0 overflow-y-auto rounded border border-orange-400/20 p-2 text-[10px] text-orange-200" data-testid="deal-book-created-warnings">
                  Created, but needs a look:
                  {result.created.filter((c) => c.warning).map((c) => <div key={c.loan_id}>{c.deal}: {c.warning}</div>)}
                </div>
              ) : null}
              {result?.failed?.length ? (
                <div className="max-h-24 shrink-0 overflow-y-auto rounded border border-red-400/20 p-2 text-[10px] text-red-300">
                  {result.failed.map((f) => <div key={f.deal}>{f.deal}: {f.reason}</div>)}
                </div>
              ) : null}
              <div role="group" aria-label="Show" className="flex shrink-0 flex-wrap items-stretch gap-1.5" data-testid="deal-book-filters">
                {filters.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    aria-pressed={filter === option.id}
                    data-filter={option.id}
                    onClick={() => setFilter(option.id)}
                    className={clsx(
                      'flex items-baseline gap-1.5 rounded border px-2 py-1 text-left text-[10px] transition',
                      filter === option.id
                        ? 'border-amber-300/50 bg-amber-300/[0.08] text-stone-100'
                        : 'border-white/[0.08] text-stone-400 hover:border-amber-300/25 hover:text-stone-200',
                    )}
                  >
                    <strong className={clsx('font-mono text-[12px]', option.tone ?? 'text-stone-200')}>{option.count}</strong>
                    <span>{option.label}</span>
                    {option.note ? <span className="text-stone-500">{option.note}</span> : null}
                  </button>
                ))}
              </div>
              <div className="min-h-[12rem] flex-1 overflow-auto rounded border border-amber-300/10 bg-[#0c0c0c]" data-testid="deal-book-grid-scroller">
                <DealBookPreviewGrid columns={shown.columns ?? []} deals={visible} outcome={outcome} />
              </div>
              {Object.keys(shown.not_imported_columns).length ? (
                <div className="flex shrink-0 items-start gap-2 text-[10px] text-stone-500" data-testid="deal-book-not-imported">
                  <AlertTriangle size={12} className="mt-0.5 shrink-0 text-orange-400" />
                  <span>
                    Not imported: {Object.entries(shown.not_imported_columns).map(([column, why]) => `${column} (${why})`).join('; ')}.
                  </span>
                </div>
              ) : (
                <p className="shrink-0 text-[10px] text-stone-500" data-testid="deal-book-not-imported">
                  Every column in the file is imported.
                </p>
              )}
            </div>
          )}
          {error && <p className="shrink-0 text-[11px] text-red-300">{error}</p>}
          <div className="flex shrink-0 justify-end gap-2 border-t border-amber-400/10 pt-3">
            <button type="button" className="btn-secondary text-[11px]" onClick={close}>{result ? 'Close' : 'Cancel'}</button>
            {!result && (
              <button type="button" className="btn-primary flex items-center gap-1.5 text-[11px]" disabled={busy || !preview?.preview_token || preview.counts.create === 0} onClick={() => void commit()}>
                <FileSpreadsheet size={12} /> {busy ? 'Working…' : `Create ${preview?.counts.create ?? 0} deal${preview?.counts.create === 1 ? '' : 's'}`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
